import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Search, X, Filter, LayoutGrid, List as ListIcon, Radio, Globe, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useProjects } from "@/hooks/use-projects";
import { useCreateProject } from "@/hooks/use-projects-mutations";
import { formatNumber } from "@/lib/utils";
import { NetworkQueryEditor } from "@/components/feature/network-query-editor";
import { ProjectCard } from "@/components/feature/project-card";
import { Badge } from "@/components/ui/badge";
import { media } from "@/lib/api";

export const Route = createFileRoute("/_auth/monitor/")({
  component: MonitorList,
});

function MonitorList() {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<"recent" | "volume" | "name">("recent");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [showModal, setShowModal] = useState(false);
  const { data: projects, isLoading } = useProjects({ q, limit: 100 });

  const sorted = projects
    ? [...projects].sort((a, b) => {
        if (sort === "volume") return b.mentions_count - a.mentions_count;
        if (sort === "name") return a.name.localeCompare(b.name);
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      })
    : [];

  const totalMentions = sorted.reduce((acc, p) => acc + p.mentions_count, 0);
  const totalNetworks = new Set(sorted.flatMap((p) => p.networks ?? [])).size;

  return (
    <div className="px-6 py-6 max-w-[1480px] mx-auto">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <p className="text-xs text-ink-500 mb-1">Monitorización</p>
          <h1 className="text-2xl font-bold text-ink-900 dark:text-white">Proyectos</h1>
          <p className="text-sm text-ink-500 mt-1">
            {sorted.length} proyectos · {totalNetworks} redes monitoreadas · {formatNumber(totalMentions)} menciones
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled>
            <Filter size={14} /> Filtros
          </Button>
          <Button size="sm" onClick={() => setShowModal(true)}>
            <Plus size={14} /> Nuevo proyecto
          </Button>
        </div>
      </header>

      <Card className="mb-5 !p-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 px-3 h-9 rounded-lg bg-ink-50 dark:bg-ink-800 flex-1 min-w-[240px]">
            <Search size={15} className="text-ink-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar proyectos, etiquetas, términos…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-ink-400 text-ink-900 dark:text-white"
            />
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white dark:bg-ink-900 ring-1 ring-ink-100 dark:ring-ink-700 text-ink-500">⌘K</span>
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as typeof sort)}
            className="h-9 px-3 rounded-lg bg-ink-50 dark:bg-ink-800 text-sm outline-none text-ink-900 dark:text-white"
          >
            <option value="recent">Ordenar: Recientes</option>
            <option value="volume">Más menciones</option>
            <option value="name">Alfabético</option>
          </select>
          <div className="ml-auto flex items-center bg-ink-50 dark:bg-ink-800 rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setView("grid")}
              className={`h-8 w-8 rounded-md flex items-center justify-center ${
                view === "grid" ? "bg-white dark:bg-ink-900 shadow-sm text-brand-600" : "text-ink-500"
              }`}
              aria-label="Vista grid"
            >
              <LayoutGrid size={14} />
            </button>
            <button
              type="button"
              onClick={() => setView("list")}
              className={`h-8 w-8 rounded-md flex items-center justify-center ${
                view === "list" ? "bg-white dark:bg-ink-900 shadow-sm text-brand-600" : "text-ink-500"
              }`}
              aria-label="Vista lista"
            >
              <ListIcon size={14} />
            </button>
          </div>
        </div>
      </Card>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-52" />)}
        </div>
      ) : sorted.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-ink-500 mb-4">No hay proyectos {q ? "que coincidan" : "aún"}.</p>
            <Button onClick={() => setShowModal(true)}><Plus size={16} /> Crear proyecto</Button>
          </CardContent>
        </Card>
      ) : view === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {sorted.map((p) => <ProjectCard key={p.id} p={p} />)}
        </div>
      ) : (
        <Card className="!p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-500 border-b border-ink-100 dark:border-ink-800">
                <th className="px-4 py-2.5 font-medium">Proyecto</th>
                <th className="px-4 py-2.5 font-medium">Redes</th>
                <th className="px-4 py-2.5 font-medium">Menciones</th>
                <th className="px-4 py-2.5 font-medium">Última actividad</th>
                <th className="px-4 py-2.5 font-medium">Creado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-ink-50 dark:border-ink-800/60 hover:bg-ink-50/60 dark:hover:bg-ink-800/30 cursor-pointer"
                  onClick={() => { window.location.href = `/monitor/${p.id}`; }}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-ink-900 dark:text-white">{p.name}</div>
                    {p.description && <div className="text-xs text-ink-500 line-clamp-1">{p.description}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {(p.networks ?? []).slice(0, 4).map((n) => (
                        <Badge key={n} variant="muted">{n}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-700 dark:text-ink-200">{formatNumber(p.mentions_count)}</td>
                  <td className="px-4 py-3 text-ink-500 text-xs whitespace-nowrap">
                    {p.last_mention_at ? new Date(p.last_mention_at).toLocaleDateString("es-PE") : "—"}
                  </td>
                  <td className="px-4 py-3 text-ink-500 text-xs whitespace-nowrap">
                    {new Date(p.created_at).toLocaleDateString("es-PE")}
                  </td>
                  <td className="px-4 py-3 text-ink-400">→</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {showModal && <NewProjectModal onClose={() => setShowModal(false)} />}
    </div>
  );
}

const KEYWORD_KINDS = ["keyword", "phrase", "hashtag", "mention", "url", "emoji"] as const;

const projectSchema = z.object({
  name: z.string().min(2, "Mínimo 2 caracteres").max(160),
  description: z.string().max(500).optional(),
  color: z.string().optional(),
});
type ProjectInput = z.infer<typeof projectSchema>;

function NewProjectModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [typeChoice, setTypeChoice] = useState<"none" | "digital">("none");
  const create = useCreateProject();
  const form = useForm<ProjectInput>({
    resolver: zodResolver(projectSchema),
    defaultValues: { color: "#2563EB" },
  });
  const [keywords, setKeywords] = useState<Array<{ expression: string; kind: string; is_exclusion: boolean }>>([]);
  const [draftKw, setDraftKw] = useState("");
  const [draftKind, setDraftKind] = useState<string>("keyword");
  const [draftExclusion, setDraftExclusion] = useState(false);
  const [networks, setNetworks] = useState<string[]>(["x", "news", "yt", "reddit"]);
  const [networkQueries, setNetworkQueries] = useState<Record<string, string>>({});
  const [clientIds, setClientIds] = useState<number[]>([]);
  const clientsQ = useQuery({
    queryKey: ["media", "clients", "active"],
    queryFn: () => media.listClients({ is_active: true, limit: 200 }),
  });

  // Step 0: selector tipo proyecto (digital vs medios tradicionales)
  if (typeChoice === "none") {
    return (
      <div className="fixed inset-0 bg-ink-950/60 z-50 flex items-center justify-center p-4 overflow-auto">
        <Card className="w-full max-w-2xl my-8">
          <CardHeader>
            <CardTitle>¿Qué quieres crear?</CardTitle>
            <CardDescription>Elige el tipo de monitoreo o campaña.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setTypeChoice("digital")}
                className="text-left p-5 rounded-xl border-2 border-ink-200 dark:border-ink-700 hover:border-brand-500 hover:bg-brand-50/40 dark:hover:bg-brand-900/10 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center mb-3">
                  <Globe size={20} className="text-brand-600" />
                </div>
                <div className="font-semibold text-ink-900 dark:text-white mb-1">Monitorización digital</div>
                <div className="text-xs text-ink-500 mb-3">
                  Captura menciones en X, Instagram, Facebook, YouTube, Reddit, news/RSS. Análisis de sentimiento, alertas, CRM social.
                </div>
                <div className="text-xs text-brand-600 font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                  Crear proyecto digital <ArrowRight size={12} />
                </div>
              </button>

              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate({ to: "/media/campaigns/new" });
                }}
                className="text-left p-5 rounded-xl border-2 border-ink-200 dark:border-ink-700 hover:border-warn hover:bg-orange-50/40 dark:hover:bg-orange-900/10 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center mb-3">
                  <Radio size={20} className="text-warn" />
                </div>
                <div className="font-semibold text-ink-900 dark:text-white mb-1">
                  Medios tradicionales
                  <Badge variant="outline" className="ml-2 text-[10px]">Agencia</Badge>
                </div>
                <div className="text-xs text-ink-500 mb-3">
                  Campañas en radio, TV, prensa escrita y digital. Trazabilidad por cliente, evidencias, reportes con link de transparencia.
                </div>
                <div className="text-xs text-warn font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                  Abrir wizard <ArrowRight size={12} />
                </div>
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <Button variant="ghost" onClick={onClose}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  function addKeyword() {
    const expr = draftKw.trim();
    if (!expr) return;
    setKeywords((prev) => [...prev, { expression: expr, kind: draftKind, is_exclusion: draftExclusion }]);
    setDraftKw("");
    setDraftExclusion(false);
  }

  function removeKeyword(i: number) {
    setKeywords((prev) => prev.filter((_, idx) => idx !== i));
  }

  function toggleClient(id: number) {
    setClientIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  async function onSubmit(data: ProjectInput) {
    try {
      await create.mutateAsync({ ...data, keywords, networks, network_queries: networkQueries, client_ids: clientIds });
      onClose();
    } catch {}
  }

  return (
    <div className="fixed inset-0 bg-ink-950/60 z-50 flex items-center justify-center p-4 overflow-auto">
      <Card className="w-full max-w-2xl my-8">
        <CardHeader>
          <CardTitle>Nuevo proyecto</CardTitle>
          <CardDescription>Define el alcance de monitorización + las keywords iniciales.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div>
              <Label>Nombre</Label>
              <Input className="mt-1" {...form.register("name")} placeholder="ej. Marca principal" />
              {form.formState.errors.name && <p className="text-xs text-bad mt-1">{form.formState.errors.name.message}</p>}
            </div>
            <div>
              <Label>Descripción (opcional)</Label>
              <Input className="mt-1" {...form.register("description")} placeholder="Para qué sirve este proyecto" />
            </div>
            <div>
              <Label>Color</Label>
              <div className="mt-1 flex gap-2">
                {["#2563EB", "#10B981", "#F97316", "#EF4444", "#A855F7", "#6B7280"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => form.setValue("color", c)}
                    className={`w-8 h-8 rounded-md ring-2 transition ${form.watch("color") === c ? "ring-ink-900 dark:ring-white" : "ring-transparent"}`}
                    style={{ background: c }}
                    aria-label={`color ${c}`}
                  />
                ))}
              </div>
            </div>

            <div className="border-t border-ink-200 dark:border-ink-800 pt-3">
              <Label>Clientes (opcional) — {clientIds.length} seleccionado{clientIds.length === 1 ? "" : "s"}</Label>
              <p className="text-xs text-ink-500 mt-0.5">Clientes cuyas redes se monitorean en este proyecto.</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(clientsQ.data ?? []).map((c) => {
                  const selected = clientIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleClient(c.id)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border transition-colors ${
                        selected
                          ? "bg-brand-50 border-brand-500 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300"
                          : "border-ink-200 dark:border-ink-700 text-ink-600 dark:text-ink-300 hover:border-brand-400"
                      }`}
                    >
                      {c.name}
                    </button>
                  );
                })}
                {!(clientsQ.data ?? []).length && (
                  <span className="text-xs text-ink-400">Sin clientes — opcional</span>
                )}
              </div>
            </div>

            <div className="border-t border-ink-200 dark:border-ink-800 pt-3">
              <NetworkQueryEditor
                networks={networks}
                networkQueries={networkQueries}
                onChange={(n, q) => { setNetworks(n); setNetworkQueries(q); }}
                onTraditionalClick={onClose}
              />
            </div>

            <div className="border-t border-ink-200 dark:border-ink-800 pt-3">
              <Label>Keywords ({keywords.length})</Label>
              <div className="mt-2 flex gap-2">
                <Input
                  value={draftKw}
                  onChange={(e) => setDraftKw(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
                  placeholder="ej. mi marca"
                />
                <select
                  value={draftKind}
                  onChange={(e) => setDraftKind(e.target.value)}
                  className="h-10 rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-2 text-sm"
                >
                  {KEYWORD_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <label className="flex items-center gap-1 text-xs text-ink-600 dark:text-ink-300">
                  <input
                    type="checkbox"
                    checked={draftExclusion}
                    onChange={(e) => setDraftExclusion(e.target.checked)}
                  /> excluir
                </label>
                <Button type="button" variant="outline" size="sm" onClick={addKeyword}>Añadir</Button>
              </div>
              {keywords.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {keywords.map((k, i) => (
                    <span
                      key={i}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs ${k.is_exclusion ? "bg-bad/10 text-bad" : "bg-ink-100 dark:bg-ink-800 text-ink-700 dark:text-ink-200"}`}
                    >
                      {k.is_exclusion && "NOT "}<strong>{k.kind}:</strong>{k.expression}
                      <button type="button" onClick={() => removeKeyword(i)} className="hover:text-bad">
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Creando…" : "Crear proyecto"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
